import {
  ChangeDetectionStrategy, Component, HostListener, computed, inject,
} from '@angular/core';
import { ConfirmService } from './confirm.service';

/**
 * Renders the currently-active {@code ConfirmService.ask()} as a
 * modal overlay. Mount once at the app root next to
 * {@code app-voyager-toast-host}.
 *
 * <p>UX: Esc = cancel, Enter = confirm (matches native dialog
 * muscle-memory). Backdrop click = cancel. Focus is trapped inside
 * the dialog via the buttons' natural tab order — no elaborate
 * focus-trap library, given the two-button surface.
 */
@Component({
  selector: 'app-confirm-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (dialog(); as d) {
      <div class="acx-backdrop" (click)="onBackdrop($event)">
        <div class="acx-modal" [class.danger]="d.variant === 'danger'"
             [class.warning]="d.variant === 'warning'"
             role="alertdialog" aria-modal="true"
             (click)="$event.stopPropagation()">
          <div class="acx-body">
            <div class="acx-title">{{ d.title }}</div>
            <div class="acx-msg">{{ d.message }}</div>
          </div>
          <div class="acx-actions">
            <button type="button" class="btn" (click)="cancel()">{{ d.cancelLabel }}</button>
            <button #ok type="button"
                    class="btn"
                    [class.primary]="d.variant === 'default'"
                    [class.danger]="d.variant === 'danger'"
                    [class.warning]="d.variant === 'warning'"
                    (click)="confirm()">{{ d.confirmLabel }}</button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .acx-backdrop {
      position: fixed; inset: 0;
      background: rgba(0, 0, 0, 0.55);
      display: flex; align-items: center; justify-content: center;
      z-index: 3000;
      animation: acx-fade 140ms ease-out;
    }
    .acx-modal {
      /* Design-system tokens (declared in styles.scss for both light and
       * dark themes). The old --fg / --muted names weren't defined here,
       * so the hardcoded light fallbacks leaked into dark mode. */
      background: var(--surface, #fff);
      color: var(--text, #111);
      border: 1px solid var(--border, transparent);
      border-radius: 14px;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
      width: min(440px, calc(100vw - 32px));
      overflow: hidden;
      animation: acx-pop 160ms cubic-bezier(0.2, 0.9, 0.3, 1.2);
    }
    .acx-modal.danger  { border-top: 3px solid var(--danger, #d64545); }
    .acx-modal.warning { border-top: 3px solid var(--warning, #d68f45); }
    .acx-body {
      padding: 22px 22px 8px 22px;
    }
    .acx-title {
      font-size: 15px; font-weight: 600; margin-bottom: 8px;
      color: var(--text, #111);
    }
    .acx-msg {
      font-size: 13px; line-height: 1.5;
      color: var(--text-muted, #5D6478);
      white-space: pre-wrap;
    }
    .acx-actions {
      padding: 16px 22px 20px 22px;
      display: flex; justify-content: flex-end; gap: 10px;
      border-top: 1px solid var(--border, transparent);
    }
    .btn.danger  { background: var(--danger, #d64545); color: #fff; border-color: var(--danger, #d64545); }
    .btn.warning { background: var(--warning, #d68f45); color: #fff; border-color: var(--warning, #d68f45); }
    @keyframes acx-fade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes acx-pop  {
      from { transform: translateY(6px) scale(0.98); opacity: 0; }
      to   { transform: translateY(0) scale(1);      opacity: 1; }
    }
  `],
})
export class ConfirmHostComponent {
  readonly service = inject(ConfirmService);
  readonly dialog = computed(() => this.service.active());

  confirm(): void {
    const d = this.dialog();
    if (d) this.service.respond(d.id, true);
  }
  cancel(): void {
    const d = this.dialog();
    if (d) this.service.respond(d.id, false);
  }
  onBackdrop(e: MouseEvent): void {
    // Only cancel when the click landed on the backdrop itself, not
    // when it bubbled up from something inside the modal.
    if (e.target === e.currentTarget) this.cancel();
  }

  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (!this.dialog()) return;
    if (e.key === 'Escape')      { e.preventDefault(); this.cancel(); }
    else if (e.key === 'Enter')  { e.preventDefault(); this.confirm(); }
  }
}
